import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Scissors, Loader2, AlertTriangle, LifeBuoy, HeartHandshake, Phone, Mail, Instagram, Facebook, Globe, Twitter, UserPlus, X, ArrowLeft, Church, BookOpen, Share2, MessageCircle, Lock, Send } from "lucide-react";
import { getPublicCreator, publicSearch, getPublicCare, yanaList, yanaSignup, yanaUploadPhoto, yanaReach, mediaUrl, shareLink } from "../lib/api";
import { UnifiedResults, MomentsView, ISSUES, ts } from "./CreatorSite";
import { getPublicMoments } from "../lib/api";
import { toast } from "sonner";

function InviteBtn({ handle, className = "" }) {
  const doInvite = async () => {
    const full = shareLink.yana(handle);
    try { if (navigator.share) { await navigator.share({ url: full, title: "Become a YANA friend — You Are Not Alone" }); return; } } catch { /* fall through */ }
    try { await navigator.clipboard.writeText(full); toast.success("Invite link copied — send it to someone who'd be a great YANA friend!"); }
    catch { window.prompt("Copy this invite link:", full); }
  };
  return (
    <button data-testid="yana-invite-btn" onClick={doInvite}
            className={`inline-flex items-center gap-2 border border-primary px-6 py-3 font-body text-sm font-semibold text-primary transition-colors hover:bg-primary hover:text-white ${className}`}>
      <Share2 className="h-4 w-4" /> Invite a friend to help
    </button>
  );
}

const CONTACT_FIELDS = [
  { key: "phone", label: "Phone", icon: Phone, ph: "+1 555 123 4567" },
  { key: "email", label: "Email", icon: Mail, ph: "you@email.com" },
  { key: "instagram", label: "Instagram", icon: Instagram, ph: "@handle or link" },
  { key: "facebook", label: "Facebook", icon: Facebook, ph: "profile link" },
  { key: "x", label: "X / Twitter", icon: Twitter, ph: "@handle or link" },
  { key: "website", label: "Website", icon: Globe, ph: "https://" },
];

function contactHref(key, val) {
  const v = (val || "").trim();
  if (key === "phone") return `tel:${v.replace(/[^\d+]/g, "")}`;
  if (key === "email") return `mailto:${v}`;
  if (v.startsWith("http")) return v;
  if (key === "instagram") return `https://instagram.com/${v.replace(/^@/, "")}`;
  if (key === "x") return `https://x.com/${v.replace(/^@/, "")}`;
  if (key === "facebook") return `https://facebook.com/${v.replace(/^@/, "")}`;
  return v.startsWith("http") ? v : `https://${v}`;
}

const CICON = { phone: Phone, email: Mail, instagram: Instagram, facebook: Facebook, x: Twitter, website: Globe, tiktok: Globe, snapchat: Globe, other: Globe };

export default function YanaHub() {
  const { handle } = useParams();
  const [creator, setCreator] = useState(null);
  const [state, setState] = useState("loading");
  const [topic, setTopic] = useState(null);
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [friends, setFriends] = useState([]);
  const [moments, setMoments] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [signupTopic, setSignupTopic] = useState(null);
  const [reachFriend, setReachFriend] = useState(null);
  const [issueTopics, setIssueTopics] = useState(ISSUES);

  useEffect(() => {
    getPublicCreator(handle).then((d) => { setCreator(d.creator); setState("ready"); }).catch(() => setState("error"));
    getPublicMoments(handle).then(setMoments).catch(() => {});
    getPublicCare(handle).then((d) => {
      const counts = {};
      Object.entries(d.topic_counts || {}).forEach(([k, n]) => {
        String(k).split(/[,/]/).forEach((part) => {
          const t = part.trim().toLowerCase();
          if (t.length >= 3 && t.length <= 26) counts[t] = (counts[t] || 0) + n;
        });
      });
      const list = Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([t]) => t);
      if (list.length) setIssueTopics(list.slice(0, 18));
    }).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    const q = params.get("issue");
    if (q) openTopic(q);
    if (params.get("join") === "1") setShowForm(true);
  }, [handle]); // eslint-disable-line react-hooks/exhaustive-deps

  const openTopic = useCallback((t) => {
    setTopic(t);
    setSearching(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    publicSearch(handle, { topic: t }).then(setResults).catch(() => setResults({ counts: {} })).finally(() => setSearching(false));
    yanaList(handle, t).then((d) => setFriends(d.friends || [])).catch(() => setFriends([]));
  }, [handle]);

  if (state === "loading") return <div className="grid h-screen place-items-center bg-background text-accent"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (state === "error") return (
    <div className="grid h-screen place-items-center bg-background text-center text-white">
      <div><AlertTriangle className="mx-auto h-8 w-8 text-primary" /><p className="mt-4 font-display text-2xl">Not found</p>
        <a href="/demo" className="mt-6 inline-block bg-accent px-6 py-3 font-body text-sm font-semibold text-black">See the demo</a></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background text-white" data-testid="yana-hub">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to={`/u/${handle}`} className="flex items-center gap-3">
            <div className="grid h-9 w-9 place-items-center bg-accent"><Scissors className="h-4 w-4 text-black" /></div>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-accent">Clip Miner</span>
          </Link>
          <Link to={`/u/${handle}`} className="flex items-center gap-1 font-mono text-xs text-accent hover:underline"><ArrowLeft className="h-3.5 w-3.5" /> Back to the show</Link>
        </div>
      </header>

      {/* Intro */}
      <section className="border-b border-border bg-primary/[0.05]">
        <div className="mx-auto max-w-6xl px-6 py-12">
          <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-primary"><LifeBuoy className="h-4 w-4" /> YANA · You Are Not Alone</p>
          <h1 className="mt-3 font-display text-4xl tracking-wide sm:text-5xl">You're not alone in this.</h1>
          <p className="mt-3 max-w-2xl font-body text-base text-muted-foreground">
            <span className="font-semibold text-white">YANA means "You Are Not Alone."</span> Two things happen here — get help, or become the help.
          </p>

          {/* How it works — two clear paths */}
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div className="border border-border bg-background/40 p-5" data-testid="yana-path-get-help">
              <p className="font-mono text-[11px] uppercase tracking-widest text-accent">1 · Get help now</p>
              <p className="mt-2 font-body text-sm text-muted-foreground">Pick what you're facing below to hear real stories, see where faith showed up, find help lines, and reach people who've been through the same thing.</p>
            </div>
            <div className="border border-primary/40 bg-primary/[0.06] p-5" data-testid="yana-path-be-help">
              <p className="font-mono text-[11px] uppercase tracking-widest text-primary">2 · Be there for someone</p>
              <p className="mt-2 font-body text-sm text-muted-foreground">Come through something hard? <span className="text-white">Sign up to be available for contact</span> so people facing it can reach you — as yourself or anonymously, publicly or by private message. Every sign-up is reviewed before it goes live.</p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button data-testid="become-yana-top" onClick={() => { setSignupTopic(null); setShowForm(true); }}
                    className="inline-flex items-center gap-2 bg-primary px-6 py-3 font-body text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">
              <UserPlus className="h-4 w-4" /> Sign up to be available for contact
            </button>
            <InviteBtn handle={handle} />
          </div>
        </div>
      </section>

      {/* Topic catalog */}
      <section className="mx-auto max-w-6xl px-6 py-10">
        <p className="font-display text-2xl tracking-wide">What are you facing?</p>
        <p className="mt-1 font-body text-sm text-muted-foreground">Tap a topic to get help — or to sign up as someone who's been there.</p>
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4" data-testid="yana-topics">
          {issueTopics.map((t) => (
            <button key={t} data-testid={`yana-issue-${t.replace(/\s+/g, "-")}`} onClick={() => openTopic(t)}
                    className={`px-4 py-3 text-left font-body text-sm capitalize transition-colors ${topic === t ? "bg-primary text-white" : "border border-border text-muted-foreground hover:border-primary hover:text-primary"}`}>
              {t}
            </button>
          ))}
        </div>
      </section>

      {/* Selected topic content */}
      {topic && (
        <section className="mx-auto max-w-6xl px-6 pb-16">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="font-display text-3xl capitalize tracking-wide text-primary">{topic}</h2>
            <button onClick={() => { setTopic(null); setResults(null); }} className="font-mono text-xs text-muted-foreground hover:text-white">clear</button>
          </div>

          {/* YANA friends for this topic */}
          <div className="mb-10">
            <div className="flex flex-col gap-3 border border-primary/40 bg-primary/[0.06] p-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="flex items-center gap-2 font-display text-xl tracking-wide"><HeartHandshake className="h-5 w-5 text-primary" /> People who've been through {topic}</h3>
                <p className="mt-1 font-body text-sm text-muted-foreground">Reach out to someone who gets it — or sign up so the next person facing {topic} can reach you.</p>
              </div>
              <button data-testid="signup-for-topic" onClick={() => { setSignupTopic(topic); setShowForm(true); }}
                      className="inline-flex shrink-0 items-center gap-2 bg-primary px-5 py-3 font-body text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">
                <UserPlus className="h-4 w-4" /> I've been through {topic} — sign me up
              </button>
            </div>
            {friends.length === 0 ? (
              <p className="mt-4 font-body text-sm text-muted-foreground">
                No one's signed up for {topic} yet. <button onClick={() => { setSignupTopic(topic); setShowForm(true); }} className="text-primary hover:underline">Be the first</button> — someone out there needs to know they're not alone.
              </p>
            ) : (
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="yana-friends">
                {friends.map((f) => <FriendCard key={f.id} f={f} onReach={() => setReachFriend(f)} />)}
              </div>
            )}
          </div>

          {/* Testimonies + help (reuses unified results: clips, stories, faith, care, helplines) */}
          <h3 className="mb-3 flex items-center gap-2 font-display text-xl tracking-wide"><LifeBuoy className="h-5 w-5 text-primary" /> Testimonies &amp; help</h3>
          {searching ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : results ? (
            <UnifiedResults results={results} query="" topic={topic} handle={handle} />
          ) : null}

          <div className="mt-10 flex flex-col items-start justify-between gap-4 border border-primary/40 bg-primary/[0.06] p-6 sm:flex-row sm:items-center">
            <p className="max-w-xl font-body text-sm text-muted-foreground">Did something here help? <span className="text-white">Someone you love might need it too.</span> Send them in — or invite a friend who's overcome this to become a YANA supporter.</p>
            <InviteBtn handle={handle} className="shrink-0" />
          </div>
        </section>
      )}

      {/* Default (no topic): Faith & Stories browsable */}
      {!topic && (
        <section className="mx-auto max-w-6xl px-6 pb-16 space-y-12">
          <MomentsView kind="faith" moments={moments} handle={handle} />
          <MomentsView kind="stories" moments={moments} handle={handle} />
        </section>
      )}

      {showForm && <SignupModal handle={handle} preselectTopic={signupTopic} onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); if (topic) openTopic(topic); }} />}
      {reachFriend && <ReachModal handle={handle} friend={reachFriend} onClose={() => setReachFriend(null)} />}
    </div>
  );
}

function FriendCard({ f, onReach }) {
  const isPrivate = f.contact_mode === "private";
  const publicContacts = Object.entries(f.contacts || {});
  return (
    <div data-testid={`friend-${f.id}`} className="border border-border p-4 transition-colors hover:border-primary">
      <div className="flex items-center gap-3">
        {f.picture ? (
          <img src={mediaUrl(f.picture)} alt={f.name} className="h-14 w-14 rounded-full border border-primary/40 object-cover" />
        ) : (
          <div className="grid h-14 w-14 place-items-center rounded-full border border-primary/40 bg-primary/10 text-primary">
            {f.anonymous ? <Lock className="h-5 w-5" /> : <span className="font-display text-xl">{(f.name || "?").slice(0, 1).toUpperCase()}</span>}
          </div>
        )}
        <div className="min-w-0">
          <p className="font-body text-sm font-semibold">{f.name}</p>
          {f.topics?.length > 0 && <p className="mt-0.5 line-clamp-1 font-mono text-[10px] uppercase tracking-wider text-primary">{f.topics.join(" · ")}</p>}
        </div>
      </div>
      {f.bio && <p className="mt-3 font-body text-xs text-muted-foreground">{f.bio}</p>}
      {isPrivate ? (
        <button data-testid={`reach-${f.id}`} onClick={onReach}
                className="mt-3 inline-flex w-full items-center justify-center gap-2 border border-primary px-3 py-2 font-body text-xs font-semibold text-primary transition-colors hover:bg-primary hover:text-white">
          <MessageCircle className="h-3.5 w-3.5" /> Reach out privately
        </button>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          {publicContacts.map(([k, v]) => {
            const Icon = CICON[k] || Globe;
            return (
              <a key={k} href={contactHref(k, v)} target="_blank" rel="noreferrer" data-testid={`contact-${k}`}
                 className="inline-flex items-center gap-1 border border-border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-accent transition-colors hover:border-accent">
                <Icon className="h-3 w-3" /> {k}
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ReachModal({ handle, friend, onClose }) {
  const [name, setName] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!message.trim()) return toast.error("Write a short message");
    if (!replyTo.trim()) return toast.error("Add a way for them to reach you back");
    setSaving(true);
    try {
      await yanaReach(handle, friend.id, { name, reply_to: replyTo, message });
      toast.success("Message sent — they'll be connected with you soon. You're not alone. 💛");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4" data-testid="yana-reach-modal">
      <div className="mt-10 w-full max-w-md border border-primary/40 bg-card p-6">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-display text-xl tracking-wide text-primary"><MessageCircle className="h-5 w-5" /> Reach {friend.name}</h3>
          <button onClick={onClose} data-testid="close-reach" className="text-muted-foreground hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <p className="mt-1 font-body text-xs text-muted-foreground">Your message goes privately to {handle}, who'll connect you. Their contact stays private until they reply.</p>
        <label className="mt-4 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Your name (optional)</label>
        <input data-testid="reach-name" value={name} onChange={(e) => setName(e.target.value)}
               className="mt-1 w-full border border-border bg-background px-3 py-2 font-body text-sm text-white outline-none focus:border-primary" />
        <label className="mt-3 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">How can they reach you back?</label>
        <input data-testid="reach-reply" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder="email or phone"
               className="mt-1 w-full border border-border bg-background px-3 py-2 font-body text-sm text-white outline-none focus:border-primary" />
        <label className="mt-3 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Your message</label>
        <textarea data-testid="reach-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
                  placeholder="Share as much or as little as you want."
                  className="mt-1 w-full border border-border bg-background px-3 py-2 font-body text-sm text-white outline-none focus:border-primary" />
        <button data-testid="reach-submit" onClick={submit} disabled={saving}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 bg-primary px-6 py-3 font-body text-sm font-semibold text-white disabled:opacity-50">
          <Send className="h-4 w-4" /> {saving ? "Sending…" : "Send private message"}
        </button>
      </div>
    </div>
  );
}

function SignupModal({ handle, preselectTopic, onClose, onDone }) {
  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [topics, setTopics] = useState(preselectTopic ? [preselectTopic] : []);
  const [display, setDisplay] = useState("public");
  const [contactMode, setContactMode] = useState("public");
  const [contacts, setContacts] = useState({});
  const [picture, setPicture] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const toggleTopic = (t) => setTopics((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]));

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { const r = await yanaUploadPhoto(handle, file); setPicture(r.path); toast.success("Photo added"); }
    catch { toast.error("Photo upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    const cleanContacts = Object.fromEntries(Object.entries(contacts).filter(([, v]) => (v || "").trim()));
    if (!name.trim()) return toast.error("Add your name");
    if (Object.keys(cleanContacts).length === 0) return toast.error("Add at least one way to reach you");
    if (topics.length === 0) return toast.error("Pick at least one topic");
    setSaving(true);
    try {
      await yanaSignup(handle, { name, bio, topics, contacts: cleanContacts, picture, display, contact_mode: contactMode });
      toast.success("You're now a YANA friend 💛 Thank you.");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not sign up"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4" data-testid="yana-signup-modal">
      <div className="mt-10 w-full max-w-lg border border-primary/40 bg-card p-6">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-display text-2xl tracking-wide text-primary"><UserPlus className="h-5 w-5" /> Become a YANA friend</h3>
          <button onClick={onClose} data-testid="close-yana-form" className="text-muted-foreground hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <p className="mt-1 font-body text-xs text-muted-foreground">Share how someone can reach you and what you can walk them through. You're offering hope — thank you.</p>

        <label className="mt-4 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Your name</label>
        <input data-testid="yana-name" value={name} onChange={(e) => setName(e.target.value)}
               className="mt-1 w-full border border-border bg-background px-3 py-2 font-body text-sm text-white outline-none focus:border-primary" />

        <label className="mt-4 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Short bio (your story helps)</label>
        <textarea data-testid="yana-bio" value={bio} onChange={(e) => setBio(e.target.value)} rows={3}
                  placeholder="e.g. 8 years sober. I know the dark. Happy to talk any time."
                  className="mt-1 w-full border border-border bg-background px-3 py-2 font-body text-sm text-white outline-none focus:border-primary" />

        <label className="mt-4 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Photo (optional)</label>
        <div className="mt-1 flex items-center gap-3">
          {picture && <img src={mediaUrl(picture)} alt="you" className="h-12 w-12 rounded-full object-cover" />}
          <input type="file" accept="image/*" onChange={onPhoto} data-testid="yana-photo" className="font-body text-xs text-muted-foreground" />
          {uploading && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
        </div>

        <label className="mt-4 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">What can you walk people through?</label>
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="yana-topic-pick">
          {ISSUES.map((t) => (
            <button key={t} onClick={() => toggleTopic(t)}
                    className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors ${topics.includes(t) ? "bg-primary text-white" : "border border-border text-muted-foreground hover:border-primary"}`}>
              {t}
            </button>
          ))}
        </div>

        <label className="mt-4 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">How can people reach you?</label>
        <p className="mt-0.5 font-body text-[11px] text-muted-foreground">Fill any. If you keep contact private below, these are only used to connect you — never shown publicly.</p>
        <div className="mt-2 space-y-2">
          {CONTACT_FIELDS.map(({ key, label, icon: Icon, ph }) => (
            <div key={key} className="flex items-center gap-2">
              <Icon className="h-4 w-4 shrink-0 text-primary" />
              <input data-testid={`yana-contact-${key}`} value={contacts[key] || ""} placeholder={`${label} — ${ph}`}
                     onChange={(e) => setContacts((p) => ({ ...p, [key]: e.target.value }))}
                     className="w-full border border-border bg-background px-3 py-1.5 font-body text-sm text-white outline-none focus:border-primary" />
            </div>
          ))}
        </div>

        {/* Privacy choices */}
        <label className="mt-5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">How should you appear?</label>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <button type="button" data-testid="display-public" onClick={() => setDisplay("public")}
                  className={`border px-3 py-2 text-left font-body text-xs ${display === "public" ? "border-primary bg-primary/10 text-white" : "border-border text-muted-foreground hover:border-primary"}`}>
            <span className="font-semibold">As myself</span><br />Name{picture ? " + photo" : ""} shown
          </button>
          <button type="button" data-testid="display-anon" onClick={() => setDisplay("anonymous")}
                  className={`border px-3 py-2 text-left font-body text-xs ${display === "anonymous" ? "border-primary bg-primary/10 text-white" : "border-border text-muted-foreground hover:border-primary"}`}>
            <span className="font-semibold">Anonymous</span><br />Shown as "A friend who's been there"
          </button>
        </div>

        <label className="mt-4 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">How do people connect with you?</label>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <button type="button" data-testid="mode-public" onClick={() => setContactMode("public")}
                  className={`border px-3 py-2 text-left font-body text-xs ${contactMode === "public" ? "border-primary bg-primary/10 text-white" : "border-border text-muted-foreground hover:border-primary"}`}>
            <span className="font-semibold">Show my contacts</span><br />People reach you directly
          </button>
          <button type="button" data-testid="mode-private" onClick={() => setContactMode("private")}
                  className={`border px-3 py-2 text-left font-body text-xs ${contactMode === "private" ? "border-primary bg-primary/10 text-white" : "border-border text-muted-foreground hover:border-primary"}`}>
            <span className="font-semibold">Private messages</span><br />They message you through {handle}
          </button>
        </div>

        <button data-testid="yana-submit" onClick={submit} disabled={saving}
                className="mt-6 w-full bg-primary px-6 py-3 font-body text-sm font-semibold text-white disabled:opacity-50">
          {saving ? "Signing up…" : "I'm in — sign me up to help"}
        </button>
      </div>
    </div>
  );
}
